import {
    Trans,
    t
} from '@lingui/macro';
import Croppie from 'croppie';
import React, {
    BaseSyntheticEvent,
    useRef, useState
} from 'react';
import { useDispatch } from 'react-redux';

import { useSelectorIsOneRosterActivatedSchool } from '../../../../../../../app';
import {
    useSelectorIsAdminUser, useSelectorIsTeacherUser
} from '../../../../../../../user-profiles';
import { fetchGetUserProfilesRequest } from '../../../../../../../user-profiles/user-profiles.actions';
import {
    closeModal, openModal
} from '../../../../../modal-manager';
import { ModalVariants } from '../../../../../modals';
import { useSelectedProfilePicture } from '../../../profile-picture-upload-modal';
import {
    showToastMessage, ToastVariants
} from '../../../user-profile-modal/component/user-profile';
import { updateProfileImage } from '../../../user-profile-modal/component/user-profile/user-profile-modal.actions';
import { CroppieStyled } from './croppie.styled';
import { selectProfilePicture } from './profile-picture.actions';
import {
    Title, Header, Close, HiddenFileInput,
    ImageDragAndDrop, Footer, Wrapper, ButtonsDivider
} from './profile-picture.styled';
import {
    getCroppedImage, dragOverHandler, isValidImageType
} from './profile-picture.utils';
import { Button } from '@components';

let croppie: Croppie | null;

export const ProfilePictureForm = () => {
    const isOneRosterActivatedSchool = useSelectorIsOneRosterActivatedSchool();
    const isAdmin = useSelectorIsAdminUser();
    const isTeacher = useSelectorIsTeacherUser();
    const showThreeEssentialsUserProfileModal = isOneRosterActivatedSchool && (isAdmin || isTeacher);
    const dispatch = useDispatch();
    const selectedProfilePicture = useSelectedProfilePicture();
    const [isCropping, setIsCropping] = useState(false);
    const inputFile = useRef<HTMLInputElement>(null);
    const handleUploadPhoto = () => {
        if (inputFile?.current) {
            inputFile.current.click();
        }
    };
    const handleFileSelection = async (selectorFiles: BaseSyntheticEvent) => {
        setIsCropping(true);
        const selectedFile = selectorFiles.target.files[0];

        croppie = await getCroppedImage(selectedFile, dispatch);
    };
    const handleOnFileDrop = async (e: DragEvent) => {
        e.preventDefault();

        if (!isValidImageType(e.dataTransfer?.files[0].type)) {
            showToastMessage(t`profile.invalidImage`, ToastVariants.error);

            return;
        }
        const selectedFile = e.dataTransfer?.items[0].getAsFile() as Blob;

        setIsCropping(true);
        croppie = await getCroppedImage(selectedFile, dispatch);
    };
    const handleCrop = () => {
        croppie && croppie.result({
            type: 'base64',
            circle: true
        }).then(async (b: string) => {
            setIsCropping(false);
            dispatch(selectProfilePicture(b));
            if (showThreeEssentialsUserProfileModal) {
                //save
                await dispatch(updateProfileImage(JSON.stringify({ profileImage: b })));
                await dispatch(fetchGetUserProfilesRequest());
            }
            await dispatch(openModal({ variant: ModalVariants.userProfile }));
        });
    };
    const handleCropSave = () => {
        croppie && croppie.result({
            type: 'base64',
            circle: true
        }).then(async (b: string) => {
            setIsCropping(false);
            await dispatch(selectProfilePicture(b));
            if (showThreeEssentialsUserProfileModal) {
                dispatch(closeModal());
                dispatch(openModal({ variant: ModalVariants.userProfile }));
                //save
                await dispatch(updateProfileImage(JSON.stringify({ profileImage: b })));
                await dispatch(fetchGetUserProfilesRequest());
            }
        });
    };

    return (
        <Wrapper>
            <Header>
                <Close
                    data-testid={'profile-picture-close'}
                    onClick={() => {
                        dispatch(openModal({ variant: ModalVariants.userProfile }));
                        croppie?.destroy();
                    }}
                >X</Close>
            </Header>
            <Title data-testid={'profile-picture-heading'}>
                <Trans id={'profile.addProfilePhoto'} />
                {isCropping ? <Close
                    data-testid={'exit-crop-mode'}
                    onClick={() => {
                        setIsCropping(false);
                        croppie?.destroy();
                    }}
                >X</Close> : null}
            </Title>
            <CroppieStyled>
                {isCropping ?
                    <Wrapper>
                        <div id={'profile-image'}></div>
                        <Footer>
                            <Button
                                data-testid={'profile-picture-upload'}
                                onClick={showThreeEssentialsUserProfileModal ? handleCropSave : handleCrop}
                                disabled={!selectedProfilePicture}
                            >
                                {showThreeEssentialsUserProfileModal ? <Trans id={'button.save'} /> : <Trans id={'profile.upload'} />}
                            </Button>
                        </Footer>
                    </Wrapper> :
                    <Wrapper>
                        <ImageDragAndDrop
                            onDrop={handleOnFileDrop}
                            onDragOver={dragOverHandler}
                        >
                            <HiddenFileInput
                                type={'file'}
                                ref={inputFile}
                                src={selectedProfilePicture}
                                accept={'image/jpg, image/jpeg, image/gif, image/png'}
                                onChange={handleFileSelection}
                                multiple={false}
                            />
                            <Trans id={'profile.dragAndDropImage'} />
                        </ImageDragAndDrop>
                        <ButtonsDivider><Trans id={'profile.or'} /></ButtonsDivider>
                        <Button onClick={handleUploadPhoto}>
                            <Trans id={'profile.uploadPhoto'} />
                        </Button>
                    </Wrapper>
                }
            </CroppieStyled>
        </Wrapper>
    );
};
